import { getDeleteApiDeviceIdMutationOptions, type DeviceViewDTO } from '@smartsash/api-client';
import { useEntityDelete } from '@smartsash/server-ops';
import { Badge, Icon, openModal } from '@smartsash/ui';
import { Link, useNavigate } from '@tanstack/react-router';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import styles from './Devices.module.css';

import { useLabNames } from '@/hooks/useLookupNames';
import { CopyableId } from '@/ui/shared/CopyableId';
import { MobileCardActions, type RowActionEntry } from '@/ui/shared/RowActions';
import { EMPTY_VALUE } from '@/utils/emptyValue';
import { firstNonEmpty } from '@/utils/firstNonEmpty';

function MobileCard({ row }: { row: DeviceViewDTO }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  // HS-869: the card/menu read the DTO the list query returns.
  const id = row.id ?? '';
  const labNames = useLabNames();
  const laboratory = firstNonEmpty(
    row.labId == null ? undefined : labNames.get(row.labId),
    row.labId,
  );
  const code = firstNonEmpty(row.identifier, row.serialNumber);
  const firmwareVersion = row.firmwareVersion ?? '';
  const osVersion = row.osVersion ?? '';
  // HS-847: delete via server-ops (invalidates the device list query + toast).
  const del = useEntityDelete({
    entity: 'device',
    mutationOptions: getDeleteApiDeviceIdMutationOptions,
    // HS-1141: the deleted record's own reads are dropped, not refetched into a 404.
    deletesRecord: true,
  });

  // HS-1177: the imperative modal replaces a `useState` pair plus a conditionally
  // rendered dialog. It resolves with the pressed action's `value`, or `null` when
  // dismissed via the overlay, Escape or the close button.
  const handleDelete = useCallback(async () => {
    const { action } = await openModal({
      title: t('devices.deleteTitle', { name: row.model }),
      description: t('devices.deleteDescription'),
      variant: 'error',
      icon: <Icon name="trash-01" />,
      actions: [
        { label: t('common.cancel'), value: 'cancel' },
        { label: t('common.delete'), value: 'confirm', destructive: true },
      ],
    }).closed;

    if (action === 'confirm') del.mutate({ id });
  }, [del, id, row.model, t]);

  // HS-1396: the keys mirror `./RowActions.tsx` entry for entry — see the note there on why the
  // two menus have to be kept in step by hand, and `../rowActionParity.test.ts` for the guard.
  const items: RowActionEntry[] = [
    {
      label: t('common.edit'),
      icon: 'edit-01',
      permission: 'device.edit',
      onClick: () => {
        void navigate({ to: '/devices/$id/edit', params: { id } });
      },
    },
    null,
    {
      label: t('devices.actions.viewLaboratory'),
      icon: 'lab',
      onClick: () => {
        void navigate({ to: '/laboratories/$id', params: { id: row.labId ?? '' } });
      },
    },
    null,
    {
      label: t('common.delete'),
      icon: 'trash-01',
      permission: 'device.delete',
      onClick: () => {
        void handleDelete();
      },
    },
  ];

  return (
    <div className={styles['m-card']}>
      <Link to="/devices/$id" params={{ id }} className={styles['m-card-title']}>
        {/* HS-1216: the same fallback the table column uses — a model-less device left the card
            with no heading at all. */}
        {firstNonEmpty(row.model, t('devices.unknownModel'))}
      </Link>
      <div className={styles['m-card-badges']}>
        <Badge variant="modern" size="sm" iconLeading={<Icon name="hash-02" />}>
          {code}
        </Badge>
        <Badge variant="modern" size="sm" iconLeading={<Icon name="lab" />}>
          {laboratory}
        </Badge>
        {/* Only when the device reported one — an empty version badge reads as broken data. */}
        {firmwareVersion !== '' && (
          <Badge variant="modern" size="sm" iconLeading={<Icon name="settings-02" />}>
            {t('devices.mobile.firmwareBadge', { value: firmwareVersion })}
          </Badge>
        )}
        {osVersion !== '' && (
          <Badge variant="modern" size="sm" iconLeading={<Icon name="settings-02" />}>
            {t('devices.mobile.osBadge', { value: osVersion })}
          </Badge>
        )}
      </div>
      <div className={styles['m-card-meta']}>
        <span className={styles['m-card-meta-label']}>{t('devices.mobile.serialNumber')}</span>
        {/* HS-1216: a label with nothing beside it reads as a rendering fault, not as "not
            reported" — the same fallback the detail page now applies to every row. */}
        <span className={styles['m-card-meta-value']}>
          {firstNonEmpty(row.serialNumber, EMPTY_VALUE)}
        </span>
      </div>
      <div className={styles['m-card-meta']}>
        <span className={styles['m-card-meta-label']}>{t('devices.mobile.identifier')}</span>
        <span className={styles['m-card-meta-value']}>
          {firstNonEmpty(row.identifier, EMPTY_VALUE)}
        </span>
      </div>
      {/*
        HS-1216: the table's leading column, kept on the card too — the table is not rendered at
        this width, so a card without the id would hide the value someone needs for `update-device-id.sh`.
      */}
      <div className={styles['m-card-meta']}>
        <span className={styles['m-card-meta-label']}>{t('devices.columns.id')}</span>
        <span className={styles['m-card-meta-value']}>
          <CopyableId id={id} />
        </span>
      </div>
      <MobileCardActions items={items} />
    </div>
  );
}

export default MobileCard;
